import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/common/PageHeader';
import PasswordRequirements from '../components/PasswordRequirements';
import { validatePassword } from '../utils/passwordValidation';
import ErrorBoundaryTester from '../components/ErrorBoundaryTester';
import './SettingsPage.css';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updatePassword, deleteAccount, loading } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  // Don't render anything if no user (will redirect)
  if (!user) {
    return null;
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    const passwordValidation = validatePassword(newPassword, confirmPassword);
    if (!passwordValidation.isValid) {
      const unmetRequirements = passwordValidation.requirements
        .filter(req => !req.met)
        .map(req => req.label);
      setError(`Password must meet the following requirements: ${unmetRequirements.join(', ')}`);
      return;
    }

    setIsChangingPassword(true);

    try {
      await updatePassword(currentPassword, newPassword);
      setPasswordChangeSuccess(true);
      // Keep the values in the fields but disable them
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm account deletion');
      return;
    }

    setIsDeletingAccount(true);
    setDeleteError(null);

    try {
      await deleteAccount(deletePassword);
      // Navigation will be handled by the auth context after successful deletion
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
      setIsDeletingAccount(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="sm" className="settings-page">
      <PageHeader title="Settings" onBack={handleBack} />
      
      <Box sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* User Information Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Information
          </Typography>
          
          <TextField
            fullWidth
            label="Email Address"
            value={user?.email || ''}
            disabled
            variant="outlined"
            sx={{ 
              mb: 2,
              '& .MuiInputBase-root.Mui-disabled': {
                backgroundColor: '#f5f5f5',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                }
              }
            }}
            helperText="Email address cannot be changed"
          />
        </Paper>

        {/* Change Password Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          
          {passwordChangeSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password updated successfully
            </Alert>
          )}
          
          <Box component="form" onSubmit={handlePasswordChange}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              variant="outlined"
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  }
                }
              }}
              required
              disabled={passwordChangeSuccess}
            />
            
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              variant="outlined"
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  }
                }
              }}
              required
              disabled={passwordChangeSuccess}
            />
            
            {/* Show password requirements */}
            {!passwordChangeSuccess && (
              <PasswordRequirements password={newPassword} confirmPassword={confirmPassword} />
            )}
            
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              variant="outlined"
              error={newPassword.length > 0 && (confirmPassword.length === 0 || newPassword !== confirmPassword)}
              helperText={
                newPassword.length > 0 && confirmPassword.length === 0 
                  ? "Please confirm your password"
                  : confirmPassword.length > 0 && newPassword !== confirmPassword 
                    ? "Passwords do not match" 
                    : ""
              }
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  }
                }
              }}
              required
              disabled={passwordChangeSuccess}
            />
            
            <Button
              type="submit"
              variant="contained"
              disabled={isChangingPassword || passwordChangeSuccess}
              startIcon={isChangingPassword ? <CircularProgress size={20} /> : null}
            >
              {isChangingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </Box>
        </Paper>

        {/* Delete Account Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="error">
            Danger Zone
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This action will <strong>permanently delete</strong> your account and all associated data including routes. This action cannot be undone.
          </Typography>
          
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{ mt: 1 }}
          >
            Delete Account
          </Button>
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!isDeletingAccount) {
            setDeleteConfirmOpen(false);
            setDeletePassword(''); // Clear password when dialog is closed
            setDeleteError(null); // Clear delete-specific errors
          }
        }}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus={true}
      >
        <DialogTitle color="error">
          Delete Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This action will <strong>permanently delete</strong> your account and all associated data including routes. This action cannot be undone.
          </DialogContentText>
          
          <DialogContentText sx={{ mb: 2 }}>
            Please enter your current password to confirm:
          </DialogContentText>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          
          <TextField
            fullWidth
            type="password"
            value={deletePassword}
            onChange={(e) => {
              setDeletePassword(e.target.value);
              // Clear error when user starts typing
              if (deleteError) {
                setDeleteError(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isDeletingAccount && deletePassword.trim()) {
                handleDeleteAccount();
              }
            }}
            placeholder="Enter your current password"
            variant="outlined"
            disabled={isDeletingAccount}
            label="Current Password"
            autoComplete="current-password"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeletePassword(''); // Clear password when canceling
              setDeleteError(null); // Clear delete-specific errors
            }}
            disabled={isDeletingAccount}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeletingAccount || !deletePassword.trim()}
            startIcon={isDeletingAccount ? <CircularProgress size={20} /> : null}
          >
            {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Boundary Testing - Development Only */}
      <ErrorBoundaryTester show={import.meta.env.DEV} />
    </Container>
  );
};
